import {
  BLACK,
  Game,
  WHITE,
  colorName,
  formatVertex,
  groupLife,
  parseVertex,
  type Color,
  type Vertex,
} from '@baduk/engine';
import type { ConceptId } from './concepts.js';

/**
 * Guided lessons: a fixed position, a goal, and an honest verdict.
 *
 * The teaching in `concepts.ts` explains ideas in words. This is where a learner
 * finds out whether they have actually understood one, which is a different
 * thing — plenty of people can recite that two eyes mean life and still not see
 * the point that makes them.
 *
 * There is no opponent. That is not a shortcut: the life-and-death reader is a
 * better referee than a bot would be. Asked to make a group live, a learner has
 * not succeeded by reaching a position where the group *might* survive if the
 * opponent blunders — they have succeeded when the group is alive whatever the
 * opponent does, which is exactly the question the reader answers. A bot reply
 * would turn a definite verdict into a game of chance against a weak player.
 *
 * So the verdict after a move is one of three things, and "not yet" is not the
 * same as "wrong": a group left unsettled is a failure precisely because the
 * other side moves next, and the lesson says so rather than waiting.
 */

export type LessonGoal =
  /** Take these stones off the board. */
  | { readonly kind: 'capture'; readonly stones: readonly string[] }
  /** Get this group out of atari — two liberties or better. */
  | { readonly kind: 'escape'; readonly group: string }
  /** Make this group unconditionally alive. */
  | { readonly kind: 'live'; readonly group: string }
  /** Leave this group with no way to live. */
  | { readonly kind: 'kill'; readonly group: string };

export interface Lesson {
  readonly id: string;
  readonly title: string;
  readonly concept: ConceptId;
  readonly size: number;
  /** What the learner is being asked to do, in one line. */
  readonly brief: string;
  readonly black: readonly string[];
  readonly white: readonly string[];
  readonly toPlay: Color;
  readonly goal: LessonGoal;
  /** Moves allowed before the attempt is judged. Almost always one. */
  readonly moves: number;
  /** Shown on request, and it points rather than answers. */
  readonly hint: string;
  /** What the learner has just proved they can see. */
  readonly reward: string;
}

export const LESSONS: readonly Lesson[] = [
  {
    id: 'first-capture',
    title: 'Take a stone',
    concept: 'capture',
    size: 9,
    brief: 'Capture the white stone in the corner.',
    black: ['B9'],
    white: ['A9'],
    toPlay: BLACK,
    goal: { kind: 'capture', stones: ['A9'] },
    moves: 1,
    hint: 'Count what the white stone has left to breathe with. There is only one point.',
    reward:
      'A stone comes off the board the moment its last liberty is filled. In the corner that takes two stones; in the middle it takes four.',
  },
  {
    id: 'run-from-atari',
    title: 'Get out of atari',
    concept: 'atari',
    size: 9,
    brief: 'The black stone has one liberty left. Save it.',
    black: ['D4'],
    white: ['C4', 'D5', 'D3'],
    toPlay: BLACK,
    goal: { kind: 'escape', group: 'D4' },
    moves: 1,
    hint: 'Running away means playing on the last liberty itself, and coming out with more than you went in with.',
    reward:
      'Extending from a stone in atari turns one liberty into three. It only works while there is somewhere open to run to — which is why the direction matters.',
  },
  {
    id: 'capture-two',
    title: 'Take the whole group',
    concept: 'capture',
    size: 9,
    brief: 'Two white stones are down to a single liberty. Capture both.',
    black: ['C4', 'C5', 'E4', 'E5', 'D6'],
    white: ['D4', 'D5'],
    toPlay: BLACK,
    goal: { kind: 'capture', stones: ['D4', 'D5'] },
    moves: 1,
    hint: 'Joined stones share their liberties, so they also die together. Find the one point they are both breathing through.',
    reward:
      'Connected stones live and die as one unit. That is why connection matters so much: it is also why a single move can take five stones as easily as one.',
  },
  {
    id: 'make-two-eyes',
    title: 'Make the group live',
    concept: 'two-eyes',
    size: 9,
    brief: 'Black has one eye space three points long. Make it two eyes.',
    black: ['B9', 'B8', 'B7', 'B6', 'A6'],
    white: ['C9', 'C8', 'C7', 'C6', 'B5', 'A5'],
    toPlay: BLACK,
    goal: { kind: 'live', group: 'B9' },
    moves: 1,
    hint: 'Three points in a row is one eye space. Which single point inside it would leave two separate eyes?',
    reward:
      'The middle of a three-point eye space is the point the whole group turns on. Playing it makes two eyes; leaving it lets the other side take it and make none.',
  },
  {
    id: 'kill-the-group',
    title: 'Take the eye space away',
    concept: 'two-eyes',
    size: 9,
    brief: 'The black group is not alive yet. Kill it.',
    black: ['B9', 'B8', 'B7', 'B6', 'A6'],
    white: ['C9', 'C8', 'C7', 'C6', 'B5', 'A5'],
    toPlay: WHITE,
    goal: { kind: 'kill', group: 'B9' },
    moves: 1,
    hint: 'The same point that would have given Black two eyes is the point that takes them both away.',
    reward:
      'The vital point cuts both ways. Whoever plays it first decides the group — which is why an unsettled group is worth more than a fight somewhere quiet.',
  },
];

/** The position a lesson starts from, ready to play. */
export function startLesson(lesson: Lesson): Game {
  const at = (label: string): Vertex => {
    const v = parseVertex(lesson.size, label);
    if (v === null) throw new Error(`Lesson ${lesson.id}: bad point ${label}`);
    return v;
  };

  return new Game({
    size: lesson.size,
    // Komi would only muddle a problem that is not about counting.
    rules: { komi: 0 },
    toPlay: lesson.toPlay,
    setup: [
      ...lesson.black.map((label) => ({ vertex: at(label), color: BLACK as Color })),
      ...lesson.white.map((label) => ({ vertex: at(label), color: WHITE as Color })),
    ],
  });
}

export type LessonStatus = 'playing' | 'solved' | 'failed';

export interface LessonResult {
  readonly status: LessonStatus;
  readonly message: string;
}

/** How the attempt stands after whatever has been played so far. */
export function checkLesson(lesson: Lesson, game: Game): LessonResult {
  const at = (label: string): Vertex => {
    const v = parseVertex(lesson.size, label);
    if (v === null) throw new Error(`Lesson ${lesson.id}: bad point ${label}`);
    return v;
  };
  const position = game.position;
  const goal = lesson.goal;
  const spent = game.moveCount >= lesson.moves;

  if (goal.kind === 'capture') {
    const left = goal.stones.filter((label) => position.at(at(label)) !== 0);
    if (left.length === 0) {
      return { status: 'solved', message: 'Captured. Those stones are off the board.' };
    }
    if (!spent) return { status: 'playing', message: 'Still on the board.' };
    return {
      status: 'failed',
      message: `${listOf(lesson.size, left.map(at))} ${left.length === 1 ? 'is' : 'are'} still breathing.`,
    };
  }

  const anchor = at(goal.group);
  const stone = position.at(anchor);

  if (goal.kind === 'kill') {
    if (stone === 0) return { status: 'solved', message: 'Captured outright.' };
    const life = groupLife(position, anchor);
    if (life?.status === 'dead') {
      return { status: 'solved', message: 'That group has no room for two eyes now.' };
    }
    if (!spent) return { status: 'playing', message: 'Still alive for now.' };
    if (life?.status === 'unsettled') {
      return {
        status: 'failed',
        message: 'Still undecided — and it is Black to play, so Black takes the point and lives.',
      };
    }
    return { status: 'failed', message: 'That group can still make two eyes.' };
  }

  // Both remaining goals are about a group of the learner's own surviving.
  if (stone === 0) {
    return { status: 'failed', message: 'The group was captured.' };
  }

  if (goal.kind === 'escape') {
    const liberties = position.libertyCount(anchor);
    if (liberties >= 2) {
      return {
        status: 'solved',
        message: `Out of atari — ${liberties} liberties now instead of one.`,
      };
    }
    if (!spent) return { status: 'playing', message: 'Still on one liberty.' };
    return { status: 'failed', message: 'Still in atari, and it can be taken next move.' };
  }

  const life = groupLife(position, anchor);
  if (life?.status === 'alive') {
    return { status: 'solved', message: 'Alive — two real eyes, and nothing can take them.' };
  }
  if (!spent) return { status: 'playing', message: 'Not alive yet.' };
  if (life?.status === 'unsettled') {
    return {
      status: 'failed',
      message: 'Still undecided — and White moves next, so White takes the vital point and kills it.',
    };
  }
  return { status: 'failed', message: 'That group has no way to make two eyes.' };
}

function listOf(size: number, points: readonly Vertex[]): string {
  const labels = points.map((v) => formatVertex(size, v));
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/** Who the learner is playing in this lesson, for the interface to say so. */
export function lessonPlayer(lesson: Lesson): string {
  return colorName(lesson.toPlay);
}
